import type { GameState } from '../logic/gameState';
import type { Worm } from '../entities/worm';

export interface WorldStabilityChecker {
  isWorldStable(): boolean;
  isWorldStuck(): boolean;
  forceWorldStable(): void;
  captureWorldSnapshot(): void;
}

export interface DamageResolutionCallbacks {
  onStartResolution: () => void;
  onWormDamageResolved: (worm: Worm, damage: number, died: boolean) => void;
  onResolutionComplete: () => void;
}

export class TurnManager {
  private stabilityTimer = 0;
  private unstableTimer = 0;
  private stuckCheckTimer = 0;
  private resolutionDelay = 0;
  private forceStableAttempts = 0;
  private damageResolutionQueue: Worm[] = [];
  private damageResolutionTotalTime = 0;

  private readonly STABILITY_WAIT_TIME = 1.5;
  private readonly MAX_UNSTABLE_TIME = 10;
  private readonly STUCK_CHECK_INTERVAL = 2;
  private readonly MAX_ABSOLUTE_UNSTABLE_TIME = 20;
  private readonly MAX_DAMAGE_RESOLUTION_TIME = 15;
  private readonly RESOLUTION_DELAY_PER_WORM = 0.8;
  private hasSnapshotForCurrentUnstable = false;

  constructor(
    private state: GameState,
    private stabilityChecker: WorldStabilityChecker,
    private callbacks: DamageResolutionCallbacks,
  ) {}

  updateDamageResolution(deltaTime: number, getWormsWithPendingDamage: () => Worm[]): void {
    const phase = this.state.phase;

    if (phase === 'damage_resolution') {
      this.processDamageResolution(deltaTime, getWormsWithPendingDamage);
    } else {
      this.checkStabilityForResolution(deltaTime, getWormsWithPendingDamage);
    }
  }

  private checkStabilityForResolution(
    deltaTime: number,
    getWormsWithPendingDamage: () => Worm[],
  ): void {
    if (this.state.phase === 'finished') {
      this.stabilityTimer = 0;
      this.unstableTimer = 0;
      this.hasSnapshotForCurrentUnstable = false;
      return;
    }

    const isStable = this.stabilityChecker.isWorldStable();
    const wormsWithPendingDamage = getWormsWithPendingDamage();
    const hasPendingDamage = wormsWithPendingDamage.length > 0;

    if (!isStable) {
      this.stabilityTimer = 0;
      this.unstableTimer += deltaTime;
      this.stuckCheckTimer += deltaTime;

      if (!this.hasSnapshotForCurrentUnstable) {
        this.stabilityChecker.captureWorldSnapshot();
        this.hasSnapshotForCurrentUnstable = true;
      }

      if (this.stuckCheckTimer >= this.STUCK_CHECK_INTERVAL) {
        this.stuckCheckTimer = 0;

        if (this.stabilityChecker.isWorldStuck()) {
          console.warn('[VVorms] World appears stuck, forcing stability');
          this.stabilityChecker.forceWorldStable();
          this.hasSnapshotForCurrentUnstable = false;
          return;
        }

        this.stabilityChecker.captureWorldSnapshot();
      }

      if (this.unstableTimer >= this.MAX_UNSTABLE_TIME && this.stabilityChecker.isWorldStuck()) {
        this.stabilityChecker.forceWorldStable();
        this.hasSnapshotForCurrentUnstable = false;
      }

      if (this.unstableTimer >= this.MAX_ABSOLUTE_UNSTABLE_TIME) {
        console.warn('[VVorms] Max absolute unstable time reached, forcing stability');
        this.stabilityChecker.forceWorldStable();
        this.hasSnapshotForCurrentUnstable = false;
      }
      return;
    }

    this.unstableTimer = 0;
    this.stuckCheckTimer = 0;
    this.hasSnapshotForCurrentUnstable = false;

    if (!hasPendingDamage) {
      this.stabilityTimer = 0;
      return;
    }

    const currentWorm = this.state.getCurrentWorm();
    const currentWormHasDamage =
      currentWorm && wormsWithPendingDamage.some((w) => w.id === currentWorm.id);
    const playerHasActed = this.state.getShotsFired() > 0 || this.state.phase === 'retreat';

    if (!currentWormHasDamage && !playerHasActed && this.state.phase === 'playing') {
      return;
    }

    this.stabilityTimer += deltaTime;

    if (this.stabilityTimer >= this.STABILITY_WAIT_TIME) {
      this.startDamageResolution(getWormsWithPendingDamage);
    }
  }

  private processDamageResolution(
    deltaTime: number,
    getWormsWithPendingDamage: () => Worm[],
  ): void {
    this.damageResolutionTotalTime += deltaTime;

    if (this.damageResolutionTotalTime >= this.MAX_DAMAGE_RESOLUTION_TIME) {
      console.warn(
        `[VVorms] Damage resolution exceeded ${this.MAX_DAMAGE_RESOLUTION_TIME}s, forcing skip to next turn`,
      );
      this.stabilityChecker.forceWorldStable();
      this.damageResolutionTotalTime = 0;
      this.hasSnapshotForCurrentUnstable = false;
      return;
    }

    if (this.resolutionDelay > 0) {
      this.resolutionDelay -= deltaTime;
      return;
    }

    if (!this.stabilityChecker.isWorldStable()) {
      this.stabilityTimer = 0;
      this.unstableTimer += deltaTime;
      this.stuckCheckTimer += deltaTime;

      if (!this.hasSnapshotForCurrentUnstable) {
        this.stabilityChecker.captureWorldSnapshot();
        this.hasSnapshotForCurrentUnstable = true;
      }

      if (this.stuckCheckTimer >= this.STUCK_CHECK_INTERVAL) {
        this.stuckCheckTimer = 0;

        if (this.stabilityChecker.isWorldStuck()) {
          console.warn('[VVorms] World appears stuck during resolution, forcing stability');
          this.stabilityChecker.forceWorldStable();
          this.hasSnapshotForCurrentUnstable = false;
          return;
        }

        this.stabilityChecker.captureWorldSnapshot();
      }

      if (this.unstableTimer >= this.MAX_UNSTABLE_TIME) {
        this.stabilityChecker.forceWorldStable();
        this.hasSnapshotForCurrentUnstable = false;
      }

      if (this.unstableTimer >= this.MAX_ABSOLUTE_UNSTABLE_TIME) {
        console.warn('[VVorms] Max absolute unstable time during resolution, forcing stability');
        this.stabilityChecker.forceWorldStable();
        this.hasSnapshotForCurrentUnstable = false;
      }
      return;
    }

    this.unstableTimer = 0;
    this.stuckCheckTimer = 0;
    this.hasSnapshotForCurrentUnstable = false;
    this.stabilityTimer += deltaTime;
    if (this.stabilityTimer < 0.5) {
      return;
    }

    if (this.damageResolutionQueue.length === 0) {
      const wormsWithPending = getWormsWithPendingDamage();
      if (wormsWithPending.length > 0) {
        this.damageResolutionQueue = wormsWithPending;
        this.stabilityTimer = 0;
      } else {
        this.state.finishDamageResolution();
        this.callbacks.onResolutionComplete();
        this.stabilityTimer = 0;
        this.unstableTimer = 0;
        return;
      }
    }

    const worm = this.damageResolutionQueue.shift();
    if (!worm || worm.state === 'dead') {
      return;
    }

    const { damage, died } = worm.resolvePendingDamage();
    this.callbacks.onWormDamageResolved(worm, damage, died);

    this.resolutionDelay = this.RESOLUTION_DELAY_PER_WORM;
    this.stabilityTimer = 0;
  }

  startDamageResolution(getWormsWithPendingDamage: () => Worm[]): void {
    this.state.startDamageResolution();
    this.damageResolutionQueue = getWormsWithPendingDamage();
    this.resolutionDelay = 0.3;
    this.stabilityTimer = 0;
    this.unstableTimer = 0;
    this.forceStableAttempts = 0;
    this.damageResolutionTotalTime = 0;
    this.callbacks.onStartResolution();
  }

  resetForceStableAttempts(): void {
    this.forceStableAttempts = 0;
  }

  incrementForceStableAttempts(): void {
    this.forceStableAttempts++;
  }

  getForceStableAttempts(): number {
    return this.forceStableAttempts;
  }

  clearDamageQueue(): void {
    this.damageResolutionQueue = [];
  }

  resetForNewTurn(): void {
    this.stabilityTimer = 0;
    this.unstableTimer = 0;
    this.stuckCheckTimer = 0;
    this.resolutionDelay = 0;
    this.forceStableAttempts = 0;
    this.damageResolutionQueue = [];
    this.damageResolutionTotalTime = 0;
    this.hasSnapshotForCurrentUnstable = false;
  }
}
